import { BaseCrud } from '@/shared/models/cruds.models'
import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { EntityIdentifiersRequest } from '@/shared/models/http/http.model'
import { AutomationPortionsGridState, initialAutomationPortionsGridState } from '@/modules/configurationModule/detail/automation/portions/components/portions-grid/configuration-automation-portions-grid.model'
import { portionsConfigurationServices } from '@/modules/configurationModule/detail/automation/portions/services/configuration-portions.service'
import { ConfigurationDefaultPortionCreateRequest, ConfigurationDefaultPortionPatchInBatchRequest, ConfigurationDefaultPortionResponse } from '@/modules/configurationModule/detail/automation/portions/services/configuration-portions.dto'
import AutomationPortionsModal from '@/modules/configurationModule/detail/automation/portions/components/portions-modal/configuration-automation-portions-modal.vue'
import AutomationPortionsModalComponent from '@/modules/configurationModule/detail/automation/portions/components/portions-modal/configuration-automation-portions-modal'
import ModalWrapper from '@/components/ModalWrapper.vue'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        AutomationPortionsModal
    }
})
export default class AutomationPortionsGrid extends BaseCrudServerSideComponent<ConfigurationDefaultPortionResponse> implements BaseCrud {
    @Ref() readonly localAutomationModalWrapper!: typeof ModalWrapper
    @Ref() readonly localAutomationModal!: AutomationPortionsModalComponent

    state: AutomationPortionsGridState
    constructor() {
        super()
        this.state = initialAutomationPortionsGridState()
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    get canUpdate(): boolean {
        return this.state.selectedRows.length === 1
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$tc('configuration.actions.update'),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => this.onUpdate(params?.node?.data)
            },
            {
                name: this.$tc('configuration.actions.delete'),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: () => this.onDelete()
            }
        ]
    }

    async createOperation(row: ConfigurationDefaultPortionCreateRequest): Promise<void> {
        const response = await this.$_toCallUnique(
            portionsConfigurationServices.createDefaultPortion(this.state.labId, row))
        if (response) {
            this.localAutomationModal.updateRow(response)
            this.state = {
                ...this.state,
                actionTitle: this.$tc('configuration.actions.update')
            }
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            this.reloadData()
        }
    }

    async patchOperation(rows: ConfigurationDefaultPortionPatchInBatchRequest[]): Promise<void> {
        const response = await this.$_toCallUnique(
            portionsConfigurationServices.patchDefaultPortion(this.state.labId, rows))
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated'))
            this.reloadData()
        }
    }

    async deleteOperation(rows: ConfigurationDefaultPortionResponse[]): Promise<void> {
        if (rows.length) {
            const selectedRowsSize = this.state.selectedRows.length
            const labelConfirmation = this.$tc('configuration.grid.deleteConfirmation')
            const labelGrid = this.$tc('configuration.grid.element', selectedRowsSize)
            const labelDeleteQuestion = this.$tc('configuration.grid.deleteQuestion')
            const labelDeleteAction = this.$tc('configuration.actions.delete')
            const promise = await this.$_showConfirmationModal(
                `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelDeleteQuestion}`,
                labelDeleteAction)
            if (promise) {
                await this._deleteBatchOperation(rows)
            }
        }
    }

    onAdd(): void {
        this.showModal(
            this.$tc('configuration.actions.add'),
            null,
            this.localAutomationModalWrapper)
    }

    onUpdate(defaultPortion: ConfigurationDefaultPortionResponse = this.state.gridApi?.getSelectedRows()[0]): void {
        if (defaultPortion) {
            this.showModal(
                this.$tc('configuration.actions.update'),
                defaultPortion,
                this.localAutomationModalWrapper)
        }
    }

    async onDelete(): Promise<void> {
        if (this.state.selectedRows.length) {
            await this.deleteOperation(this.state.selectedRows)
        }
    }

    private async _deleteBatchOperation(rows: ConfigurationDefaultPortionResponse[]): Promise<void> {
        const rowsToDelete: EntityIdentifiersRequest = {
            entityIdentifiers: rows.map((template: ConfigurationDefaultPortionResponse) => Number(template?.id))
        }
        await this.$_toCallUnique(
            portionsConfigurationServices.deleteDefaultPortion(this.state.labId, rowsToDelete))
        if (rows.length > 1) {
            this.$_showToastSuccess(
                this.$tc('configuration.toastMessages.deleted', 2, { elements: rows.length }))
        } else {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted'))
        }

        this.reloadData()
    }
}
